import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormControl } from '@angular/forms';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { AllServicesService } from '../../../services/all-services.service';
import { SharedServiceService } from '../../services/shared-service.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { startWith, map } from 'rxjs/operators';
import { NotificationFacadeService } from '../../services/notification-facade.service';
import { filter, firstValueFrom } from 'rxjs';
import { mapUnknownError } from '../../services/app-error-catalog';

@Component({
  selector: 'app-bc-fob-m-base-oil',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ScrollingModule,
    MatFormFieldModule, MatInputModule, MatAutocompleteModule],
  templateUrl: './bc-fob-m-base-oil.component.html',
  styleUrl: './bc-fob-m-base-oil.component.scss'
})
export class BcFobMBaseOilComponent implements OnInit, OnChanges {
  @Input() baseOilData: any = null; // Receive data from the parent


  baseOilControls: FormControl[] = [];
  batchControls: FormControl[] = [];
  filteredBaseOils: any[][] = [];
  filteredBatches: any[][] = [];
  // Variables to store the last valid selections
  lastValidSelections: any[] = [];
  rows: any[] = [
    {
      oil: '', batch: '', ratio1: '', treat1: '', ratio2: '', treat2: '', availableBatches: [],
      group: '', saturate: '', sulfur: '', KV100cSt: '', KV40: '', VI: '', NOACK: '', CCS: ''
    }
  ];
  maxRows: number = 6;
  minRows: number = 3;
  data: any[] = [];
  baseOilDropdown: any[] = [];
  batchDropdown: any[] = [];
  baseOilToBatchMap: Map<string, any[]> = new Map();
  selectedBaseOil: string = '';
  selectedBatch: string = '';

  constructor(private dataService: AllServicesService,
    private sharedService: SharedServiceService,
    private notificationFacade: NotificationFacadeService
  ) { }

  private formatToTwoDecimals(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const parsed = typeof value === 'string' ? parseFloat(value) : Number(value);
    if (!Number.isFinite(parsed)) {
      return '';
    }
    const rounded = Math.round((parsed + Number.EPSILON) * 100) / 100;
    return rounded.toFixed(2);
  }

  private sanitizeNumericInputValue(value: string): string {
    if (!value) {
      return '';
    }
    const cleaned = value.replace(/[^0-9.]/g, '');
    const parts = cleaned.split('.');
    if (parts.length <= 1) {
      return cleaned;
    }
    return `${parts[0]}.${parts.slice(1).join('')}`;
  }

  restrictNumericInput(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Tab', 'Enter', 'Home', 'End'
    ];
    if (allowedKeys.includes(event.key)) {
      return;
    }
    if (!/^[0-9.]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  handleRatioInput(event: Event, rowIndex: number, field: 'ratio1' | 'ratio2'): void {
    const target = event.target as HTMLInputElement;
    const cleaned = this.sanitizeNumericInputValue(target.value);
    if (cleaned !== target.value) {
      target.value = cleaned;
    }
    this.rows[rowIndex][field] = cleaned;
    this.calculateAndEmitTotals();
    this.emitSelectedBaseOilData();
  }

  onRatioBlurFormat(rowIndex: number, field: 'ratio1' | 'ratio2'): void {
    const capped = this.applyRatioLimits(this.rows[rowIndex][field]);
    const formatted = this.formatToTwoDecimals(capped);
    this.rows[rowIndex][field] = formatted;
    if (this.isTotalRatioComplete(field) || this.isTotalRatioZero(field)) {
      if (field === 'ratio1') {
        this.calculateBaseOilProportionsBlend1();
      } else {
        this.calculateBaseOilProportionsBlend2();
      }
    }
    this.emitSelectedBaseOilData();
  }

  private isTotalRatioComplete(field: 'ratio1' | 'ratio2'): boolean {
    const total = this.rows.reduce((sum, row) => {
      const value = parseFloat(row[field]);
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);
    return Math.abs(total - 100) < 0.0001;
  }

  private isTotalRatioZero(field: 'ratio1' | 'ratio2'): boolean {
    const total = this.rows.reduce((sum, row) => {
      const value = parseFloat(row[field]);
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);
    return total === 0;
  }

  ngOnInit(): void {
    this.clearRows();
    this.addRow();
    this.addRow();
    this.addRow();
    this.sharedService.getTotalsUpdates().subscribe((update: { component: string; action: string }) => {
      if (update.component !== 'baseOil' && update.action === 'update') {
        this.calculateBaseOilProportionsBlend1();
        this.calculateBaseOilProportionsBlend2();
      }
    });

    // Hydrate base oil selections when a favorite is imported
    this.sharedService.ImpFavoriteFlag$
      .pipe(filter(flag => flag === true))
      .subscribe(() => this.hydrateBaseOilsFromImport());
  }

  clearRows() {
    this.rows = [];
    this.baseOilControls = [];
    this.batchControls = [];
    this.filteredBaseOils = [];
    this.filteredBatches = [];
    this.lastValidSelections = [];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['baseOilData'] && this.baseOilData) {
      this.processDropdownData();
    }
  }

  // Process the data received from the parent
  processDropdownData(): void {
    if (this.baseOilData) {
      // Create the base oil list for the autocomplete
      this.baseOilDropdown = Array.from(
        new Set(this.baseOilData.map((item: any) => item['Base Oil']))
      )
        .map(baseOil => ({
          label: baseOil as string,
          value: baseOil as string
        }));

      // Create a map from each base oil to its corresponding batches
      this.baseOilToBatchMap = new Map<string, any[]>();

      this.baseOilData.forEach((item: any) => {
        const baseOil = item['Base Oil'];
        const batch = {
          value: item.Batch
        };

        if (!this.baseOilToBatchMap.has(baseOil)) {
          this.baseOilToBatchMap.set(baseOil, []);
        }

        // Avoid duplicates
        const existingBatches = this.baseOilToBatchMap.get(baseOil) || [];
        if (!existingBatches.some(b => b.value === batch.value)) {
          this.baseOilToBatchMap.get(baseOil)?.push(batch);
        }
      });

      // Initialize filteredBaseOils for each row
      this.rows.forEach((row, index) => {
        this.filteredBaseOils[index] = [...this.baseOilDropdown];
        this.updateBatchOptions(index);
      });
    }
  }

  // Method to filter options based on the entered text
  private _filter(value: string | any): any[] {
    if (!value) return this.baseOilDropdown.slice();

    // If the value is an object (already selected), use its label
    const filterValue = typeof value === 'string'
      ? value.toLowerCase()
      : (value.label ? value.label.toLowerCase() : '');

    return this.baseOilDropdown.filter(option =>
      option.label.toLowerCase().includes(filterValue)
    );
  }

  // Method to display the text in the autocomplete
  displayFn(item: any): string {
    return item && item.label ? item.label : '';
  }

  // When the input loses focus
  onBaseOilInputBlur(rowIndex: number): void {
    const currentValue = this.baseOilControls[rowIndex].value;
    if (!this.isValidBaseOilSelection(currentValue)) {
      this.baseOilControls[rowIndex].setValue(this.lastValidSelections[rowIndex], { emitEvent: false });
    }
  }

  // Validates whether the current value is a valid selection
  private isValidBaseOilSelection(value: any): boolean {
    if (!value) return true; // Allow empty values

    // If it is an object with a value property
    if (typeof value === 'object' && value.value) {
      return this.baseOilDropdown.some(option => option.value === value.value);
    }

    return false; // If it is a string or another type, it is not valid
  }

  private updateBatchOptions(rowIndex: number): void {
    const selectedBaseOil = this.rows[rowIndex].oil;
    this.rows[rowIndex].availableBatches = this.baseOilToBatchMap.get(selectedBaseOil) || [];
    this.filteredBatches[rowIndex] = [...this.rows[rowIndex].availableBatches];
  }

  isBatchUnavailable(rowIndex: number): boolean {
    const row = this.rows[rowIndex];
    return !!row?.oil && !!row.batch && !row.availableBatches.some(
      (batch: { value: string }) => batch.value === row.batch
    );
  }

  updateBatchDropdown(rowIndex: number): void {
    const selectedBaseOil = this.rows[rowIndex].oil;
    this.updateBatchOptions(rowIndex);

    if (selectedBaseOil && this.baseOilToBatchMap.has(selectedBaseOil)) {
      const typicalBatch = this.rows[rowIndex].availableBatches.find(
        (batch: any) => batch.value === 'TYPICAL');

      if (typicalBatch) {
        this.rows[rowIndex].batch = 'TYPICAL';
        this.batchControls[rowIndex]?.setValue('TYPICAL', { emitEvent: false });
        // Automatically call onBatchSelected to load properties
        this.onBatchSelected(rowIndex);
      } else {
        this.rows[rowIndex].batch = '';
        this.batchControls[rowIndex]?.setValue('', { emitEvent: false });
      }
    } else {
      this.rows[rowIndex].availableBatches = [];
      this.rows[rowIndex].batch = '';
      this.filteredBatches[rowIndex] = [];
      this.batchControls[rowIndex]?.setValue('', { emitEvent: false });
    }

    this.emitSelectedBaseOilData();
  }

  onBatchInput(rowIndex: number, value: string): void {
    const options = this.rows[rowIndex]?.availableBatches || [];
    const normalized = (value || '').toLowerCase();
    this.filteredBatches[rowIndex] = options.filter((option: any) =>
      String(option?.value || '').toLowerCase().includes(normalized)
    );
  }

  onBatchInputBlur(rowIndex: number): void {
    const control = this.batchControls[rowIndex];
    const typedValue = String(control?.value || '').trim();
    const options = this.rows[rowIndex]?.availableBatches || [];
    const exactMatch = options.find((option: any) =>
      String(option?.value || '').toLowerCase() === typedValue.toLowerCase()
    );

    if (!typedValue) {
      this.rows[rowIndex].batch = '';
      control?.setValue('', { emitEvent: false });
      this.emitSelectedBaseOilData();
      return;
    }

    if (exactMatch) {
      this.rows[rowIndex].batch = exactMatch.value;
      control?.setValue(exactMatch.value, { emitEvent: false });
      this.onBatchSelected(rowIndex);
      return;
    }

    const fallback = this.rows[rowIndex].batch || '';
    this.rows[rowIndex].batch = fallback;
    control?.setValue(fallback, { emitEvent: false });
    this.onBatchInput(rowIndex, fallback);
  }

  onBatchSelectedFromAutocomplete(event: any, rowIndex: number): void {
    const selected = String(event.option.value || '').trim();
    this.rows[rowIndex].batch = selected;
    this.batchControls[rowIndex]?.setValue(selected, { emitEvent: false });
    this.onBatchSelected(rowIndex);
  }

  // New method: call get_Batch_data when the user selects a batch
  onBatchSelected(rowIndex: number): void {
    const row = this.rows[rowIndex];

    // Only run if Base Oil and Batch are selected
    if (!row.oil || !row.batch) {
      return;
    }

    // FIX: Backend ALWAYS expects 6 elements in each array (fill empty rows with 'NaN')
    // Extract exactly 6 rows (assuming maxRows = 6)
    const baseOils: string[] = [];
    const batches: string[] = [];

    // Take the first 6 rows, filling empty ones with 'NaN'
    for (let i = 0; i < 6; i++) {
      if (i < this.rows.length && this.rows[i].oil && this.rows[i].batch) {
        baseOils.push(this.rows[i].oil);
        batches.push(this.rows[i].batch);
      } else {
        baseOils.push('NaN');
        batches.push('NaN');
      }
    }

    const payload = { base: baseOils, batch: batches };

    // Call the backend to fetch properties
    this.dataService.get_Batch_data(payload).subscribe({
      next: (response: any[]) => {
        // Update rows with properties from the backend
        response.forEach((boData: any) => {
          // Filter out 'NaN' from the backend
          if (boData['Base Oil'] === 'NaN' || !boData['Base Oil']) {
            return;
          }

          const matchingRow = this.rows.find(
            r => r.oil === boData['Base Oil'] && r.batch === boData['Batch']
          );

          if (matchingRow) {
            // Store all properties returned by get_Batch_data
            matchingRow.group = boData['Group'] || '';
            matchingRow.saturate = boData['Saturate'] || '';
            matchingRow.sulfur = boData['Sulfur'] || '';
            matchingRow.KV100cSt = boData['KV100'] || '';
            matchingRow.KV40 = boData['KV40'] || '';
            matchingRow.VI = boData['VI'] || '';
            matchingRow.NOACK = boData['NOACK'] || '';
            matchingRow.CCS = boData['CCS'] || '';

          }
        });

        // Emit updated data to the shared service
        this.emitSelectedBaseOilData();
      },
      error: (err) => {
        console.error('❌ Error fetching batch data:', err);
         this.notificationFacade.showError(mapUnknownError(err, 'loadBatchProperties'));
      }
    });
  }

  emitSelectedBaseOilData() {
    this.sharedService.setBaseOilSelectedData(this.rows);
  }

  private applyRatioLimits(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const parsed = typeof value === 'string' ? parseFloat(value) : Number(value);
    if (!Number.isFinite(parsed)) {
      return '';
    }
    if (parsed > 100) {
      return '100';
    }
    if (parsed < 0) {
      return '0';
    }
    return String(parsed);
  }

  // New method to calculate the treat for a single row while typing
  calculateTreatForSingleRow(rowIndex: number, ratioField: 'ratio1' | 'ratio2', treatField: 'treat1' | 'treat2'): void {
    const row = this.rows[rowIndex];

    // If no Base Oil is selected or the ratio is empty, set treat to 0
    if (!row.oil || !row[ratioField] || parseFloat(row[ratioField]) <= 0) {
      row[treatField] = '';
      this.calculateAndEmitTotals();
      return;
    }

    // Get the total available for Base Oil
    const otherComponentsTotal = ratioField === 'ratio1'
      ? this.getOtherComponentsTotalForBlend1()
      : this.getOtherComponentsTotalForBlend2();

    const availableForBaseOil = Math.max(0, 100 - otherComponentsTotal);

    // If nothing is available, we cannot assign
    if (availableForBaseOil <= 0) {
      row[treatField] = '';
      this.calculateAndEmitTotals();
      return;
    }

    // Get the proportion directly from the ratio being typed
    const currentRatio = parseFloat(row[ratioField]);

    // Assign treat proportionally to what is available based on the current ratio
    const treat = (currentRatio / 100) * availableForBaseOil;
    row[treatField] = treat.toFixed(2);

    // Update totals
    this.calculateAndEmitTotals();
  }

  // Add a handler for when the input loses focus
  onRatioBlur(field: 'ratio1' | 'ratio2'): void {
    // Redistribute proportionally across all rows
    if (field === 'ratio1') {
      this.calculateBaseOilProportionsBlend1();
    } else {
      this.calculateBaseOilProportionsBlend2();
    }
  }

  // Update calculateBaseOilProportionsBlend1 to respect redistribution
  calculateBaseOilProportionsBlend1(): void {
    // 1. Get totals from other components for Blend 1
    const otherComponentsTotal = this.getOtherComponentsTotalForBlend1();

    // 2. Calculate what remains available for Base Oil (difference to 100%)
    const availableForBaseOil = Math.max(0, 100 - otherComponentsTotal);

    // 3. Keep only rows with Base Oil selected and ratio > 0
    const validRows = this.rows.filter(row =>
      row.oil && row.ratio1 && parseFloat(row.ratio1) > 0
    );

    // 4. If there are no valid rows, clear all treats
    if (validRows.length === 0) {
      this.rows.forEach(row => { row.treat1 = ''; });
      this.calculateAndEmitTotals();
      return;
    }

    // 5. Calculate the total sum of valid ratios
    let totalRatio = validRows.reduce((sum, row) =>
      sum + parseFloat(row.ratio1), 0
    );

    if (Math.abs(totalRatio - 100) >= 0.0001) {
      this.calculateAndEmitTotals();
      return;
    }

    // 6. Clear treats for rows without a ratio or with a ratio of 0
    this.rows.forEach(row => {
      if (!row.oil || !row.ratio1 || parseFloat(row.ratio1) <= 0) {
        row.treat1 = '';
      }
    });

    // 7. Distribute the available percentage proportionally across valid rows
    validRows.forEach(row => {
      const ratio = parseFloat(row.ratio1);
      const proportion = (ratio / totalRatio) * availableForBaseOil;

      // Find the original row and update it
      const originalRow = this.rows.find(r => r === row);
      if (originalRow) {
        originalRow.treat1 = proportion.toFixed(2);
      }
    });

    // 8. Emit the new totals
    this.calculateAndEmitTotals();
  }

  addRow() {
    if (this.rows.length < this.maxRows) {
      const newIndex = this.rows.length;

      // Create a new control
      const newControl = new FormControl();
      const newBatchControl = new FormControl('');
      this.baseOilControls.push(newControl);
      this.batchControls.push(newBatchControl);

      // Initialize filtered options for this row
      this.filteredBaseOils[newIndex] = [...this.baseOilDropdown];
      this.filteredBatches[newIndex] = [];

      // Set up the filtering observable
      newControl.valueChanges.pipe(
        startWith(''),
        map(value => typeof value === 'string' ? value : value?.label),
        map(name => name ? this._filter(name) : this.baseOilDropdown.slice())
      ).subscribe(filtered => {
        this.filteredBaseOils[newIndex] = filtered;
      });

      // Initialize lastValidSelection for this row
      this.lastValidSelections[newIndex] = null;

      newBatchControl.valueChanges.pipe(
        startWith(''),
        map(value => String(value || ''))
      ).subscribe(value => {
        this.onBatchInput(newIndex, value);
      });

      // Add the row
      this.rows.push({
        oil: '',
        batch: '',
        ratio1: '',
        treat1: '',
        ratio2: '',
        treat2: '',
        availableBatches: [],
        group: '',
        saturate: '',
        sulfur: '',
        KV100cSt: '',
        KV40: '',
        VI: '',
        NOACK: '',
        CCS: ''
      });
    }
  }

  removeRow() {
    if (this.rows.length > this.minRows) {
      const lastIndex = this.rows.length - 1;
      this.clearRowData(lastIndex);
      this.rows.pop();
      // Keep form controls and filtered lists in sync
      this.baseOilControls.pop();
      this.batchControls.pop();
      this.filteredBaseOils.pop();
      this.filteredBatches.pop();
      this.lastValidSelections.pop();
      this.emitSelectedBaseOilData();
    }
  }

  canAddRow(): boolean {
    return this.rows.length < this.maxRows;
  }

  canRemoveRow(): boolean {
    return this.rows.length > this.minRows;
  }

  clearRowData(index: number): void {
    this.rows[index].oil = '';
    this.rows[index].batch = '';
    this.rows[index].ratio1 = '';
    this.rows[index].treat1 = '';
    this.rows[index].ratio2 = '';
    this.rows[index].treat2 = '';
    this.rows[index].availableBatches = [];
    this.rows[index].group = '';
    this.rows[index].saturate = '';
    this.rows[index].sulfur = '';
    this.rows[index].KV100cSt = '';
    this.rows[index].KV40 = '';
    this.rows[index].VI = '';
    this.rows[index].NOACK = '';
    this.rows[index].CCS = '';

    // Clear control
    if (this.baseOilControls[index]) {
      // Reset control value and allow valueChanges to emit so the filtering pipeline restores the full list
      this.baseOilControls[index].setValue(null);
      this.lastValidSelections[index] = null;
      // Also explicitly reset the filtered options (defensive) so the autocomplete shows the full list again
      this.filteredBaseOils[index] = [...this.baseOilDropdown];
    }
    if (this.batchControls[index]) {
      this.batchControls[index].setValue('');
      this.filteredBatches[index] = [];
    }

    // Recalculate proportions after clearing a row
    this.calculateBaseOilProportionsBlend1();
    this.calculateBaseOilProportionsBlend2();
    this.calculateAndEmitTotals();

    // Emit changes
    this.emitSelectedBaseOilData();
  }

  // Add method to calculate and send totals
  calculateAndEmitTotals(): void {
    let totalRatioP1 = 0;
    let totalTreatP1 = 0;
    let totalRatioP2 = 0;
    let totalTreatP2 = 0;

    // Calculate totals for Base Oil
    this.rows.forEach(row => {
      if (row.oil && row.ratio1 && !isNaN(row.ratio1)) {
        totalRatioP1 += parseFloat(row.ratio1);
      }
      if (row.oil && row.treat1 && !isNaN(row.treat1)) {
        totalTreatP1 += parseFloat(row.treat1);
      }
      if (row.oil && row.ratio2 && !isNaN(row.ratio2)) {
        totalRatioP2 += parseFloat(row.ratio2);
      }
      if (row.oil && row.treat2 && !isNaN(row.treat2)) {
        totalTreatP2 += parseFloat(row.treat2);
      }
    });

    // Send totals to the service
    this.sharedService.updateComponentTotals(
      'baseOil',
      totalTreatP1,
      totalTreatP2,
      totalRatioP1,
      totalRatioP2
    );
  }

  // Get total treat% from other components for Blend 1
  private getOtherComponentsTotalForBlend1(): number {
    const totals = this.sharedService.getAllComponentTotals();
    let total = 0;

    // Sumar los treats de todos los componentes excepto base oil
    if (totals.package && totals.package.treatP1) {
      total += totals.package.treatP1;
    }
    if (totals.vm && totals.vm.treatP1) {
      total += totals.vm.treatP1;
    }
    if (totals.ppd && totals.ppd.treatP1) {
      total += totals.ppd.treatP1;
    }
    if (totals.additives && totals.additives.treatP1) {
      total += totals.additives.treatP1;
    }

    return total;
  }

  calculateBaseOilProportionsBlend2(): void {
    // 1. Get totals from other components for Blend 2
    const otherComponentsTotal = this.getOtherComponentsTotalForBlend2();

    // 2. Calculate what remains available for Base Oil (difference to 100%)
    const availableForBaseOil = Math.max(0, 100 - otherComponentsTotal);

    // 3. Keep only rows with Base Oil selected and ratio > 0
    const validRows = this.rows.filter(row =>
      row.oil && row.ratio2 && parseFloat(row.ratio2) > 0
    );

    // 4. If there are no valid rows, clear all treats
    if (validRows.length === 0) {
      this.rows.forEach(row => { row.treat2 = ''; });
      this.calculateAndEmitTotals();
      return;
    }

    // 5. Calculate the total sum of valid ratios
    let totalRatio = validRows.reduce((sum, row) =>
      sum + parseFloat(row.ratio2), 0
    );

    if (Math.abs(totalRatio - 100) >= 0.0001) {
      this.calculateAndEmitTotals();
      return;
    }

    // 6. Clear treats for rows without a ratio or with a ratio of 0
    this.rows.forEach(row => {
      if (!row.oil || !row.ratio2 || parseFloat(row.ratio2) <= 0) {
        row.treat2 = '';
      }
    });

    // 7. Distribute the available percentage proportionally among valid rows
    validRows.forEach(row => {
      const ratio = parseFloat(row.ratio2);
      const proportion = (ratio / totalRatio) * availableForBaseOil;

      // Find the original row and update it
      const originalRow = this.rows.find(r => r === row);
      if (originalRow) {
        originalRow.treat2 = proportion.toFixed(2);
      }
    });

    // 8. Emit the new totals
    this.calculateAndEmitTotals();
  }

  // Get total treat% from other components for Blend 2
  private getOtherComponentsTotalForBlend2(): number {
    const totals = this.sharedService.getAllComponentTotals();
    let total = 0;

    // Sum treats of all components except base oil
    if (totals.package && totals.package.treatP2) {
      total += totals.package.treatP2;
    }
    if (totals.vm && totals.vm.treatP2) {
      total += totals.vm.treatP2;
    }
    if (totals.ppd && totals.ppd.treatP2) {
      total += totals.ppd.treatP2;
    }
    if (totals.additives && totals.additives.treatP2) {
      total += totals.additives.treatP2;
    }

    return total;
  }

  onBaseOilSelected(event: any, rowIndex: number): void {
    const selectedBaseOil = event.option.value;

    // Save the valid selection
    this.lastValidSelections[rowIndex] = selectedBaseOil;

    // Update the control
    this.baseOilControls[rowIndex].setValue(selectedBaseOil, { emitEvent: false });

    // Update the row
    this.rows[rowIndex].oil = selectedBaseOil.value;
    this.rows[rowIndex].batch = '';
    this.batchControls[rowIndex]?.setValue('', { emitEvent: false });

    // Update the available batches
    this.updateBatchDropdown(rowIndex);

    // Recalculate proportions after selecting a base oil
    this.calculateBaseOilProportionsBlend1();
    this.calculateBaseOilProportionsBlend2();

    // Emit selected data
    this.emitSelectedBaseOilData();
  }

  
  /**
   * Hydrate base oil selections from imported favorite data.
   * Reads finishedoil_data$ to find Base oil rows, matches against dropdown,
   * selects them programmatically with batch, and triggers property loading.
   */
  private async hydrateBaseOilsFromImport(): Promise<void> {
    // Clear existing state before importing
    this.clearRows();
    this.addRow();
    this.addRow();
    this.addRow();

    const foData = await firstValueFrom(this.sharedService.finishedoil_data$);
    if (!foData || !Array.isArray(foData) || foData.length === 0) return;
    if (!this.baseOilDropdown || this.baseOilDropdown.length === 0) return;

    const boRows = foData.filter((row: any) =>
      row.Fixed_value && row.Fixed_value.startsWith('Base oil')
    );

    for (let i = 0; i < boRows.length; i++) {
      const row = boRows[i];
      const boName = row.Name;
      if (!boName) continue;

      // Ensure we have enough rows
      while (this.rows.length <= i) {
        this.addRow();
      }

      // Find matching base oil in dropdown
      const match = this.baseOilDropdown.find(opt => opt.value === boName);
      if (!match) continue;

      // Set form control and row state
      this.baseOilControls[i].setValue(match, { emitEvent: false });
      this.lastValidSelections[i] = match;
      this.rows[i].oil = boName;

      // Set batch
      const batchName = row.Batch || 'TYPICAL';
      this.updateBatchOptions(i);
      this.rows[i].batch = batchName;
      this.batchControls[i].setValue(batchName, { emitEvent: false });

      // Set ratios and treat weights
      if (row.Treat_wt_ratio_P1) this.rows[i].ratio1 = row.Treat_wt_ratio_P1;
      if (row.Treat_wt_P1) this.rows[i].treat1 = row.Treat_wt_P1;
      if (row.Treat_wt_ratio_P2) this.rows[i].ratio2 = row.Treat_wt_ratio_P2;
      if (row.Treat_wt_P2) this.rows[i].treat2 = row.Treat_wt_P2;

    }

    this.emitSelectedBaseOilData();
    this.calculateBaseOilProportionsBlend1();
    this.calculateBaseOilProportionsBlend2();
    this.calculateAndEmitTotals();

    const selectedRowIndex = this.rows.findIndex(row => row.oil && row.batch);
    if (selectedRowIndex >= 0) {
      this.onBatchSelected(selectedRowIndex);
    }
  }
}
